/*
 * File: SchedulingPanel.tsx
 * Author: Austin Robinson
 * Date Created: 9/4/2022
 * Copyright (c) DrivingDoc 2022
 */
import { useState } from 'react';

import UndoIcon from '@mui/icons-material/Undo';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/GridLegacy';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import { listCourseSectionsOptions, listCoursesOptions } from '@drivingdoc/api-client/generated/react-query';
import { Course } from '@drivingdoc/api-client/generated/types';
import { Instructor } from 'types/instructors';
import { GetSectionsItem } from 'types/sections';

import { CreateCoursePeriodsOptions } from './Scheduler.ts';
import './styles.css';

export const SCHEDULING_PANEL_WIDTH = 250;

type EventCountObject = {
  existingEvents: number;
  newEvents: number;
};

type PropTypes = {
  open: boolean;
  disabled?: boolean;
  options: CreateCoursePeriodsOptions;
  setOptions: (options: CreateCoursePeriodsOptions) => void;
  setSelectedCourseType: (type: 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online') => void;
  instructors: Instructor[];
  visibleResources: Set<string>;
  toggleResource: (resourceID: string) => void;
  eventCount: (resourceID: string) => EventCountObject;
  disableSubmit?: boolean;
  onSubmit?: () => void;
  showUndo?: boolean;
  onUndo?: () => void;
  // setVisibleInstructors: (instructorIDs: Array<string | null>) => void;
};

export default function SchedulingPanel({
  open,
  disabled = false,
  options,
  setOptions,
  setSelectedCourseType,
  instructors,
  visibleResources,
  toggleResource,
  eventCount,
  disableSubmit,
  onSubmit,
  showUndo,
  onUndo,
}: PropTypes) {
  // const [checked, setChecked] = useState(['']);
  const { data: courses } = useQuery({
    ...listCoursesOptions(),
    initialData: [],
  });

  const { data: sections } = useQuery({
    ...listCourseSectionsOptions({
      query: {
        courseId: options.course_id,
        page: 1,
        pageSize: 100,
      },
    }),
    enabled: !!options.course_id,
  });
  const sectionRows = (sections as { data: GetSectionsItem[]; total: number } | undefined)?.data;

  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  // const [selectedSection, setSelectedSection] = useState<GetSectionsItem | null>(null);

  const handleToggle = (value: string) => () => {
    if (!disabled) {
      toggleResource(value);
    }
  };

  const getTotalEventCount = (resourceID: string) => {
    const resourceEvents = eventCount(resourceID);
    return resourceEvents.existingEvents + resourceEvents.newEvents;
  };

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    // padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-start',
  }));

  return (
    <Drawer
      sx={{
        width: SCHEDULING_PANEL_WIDTH,
        '& .MuiDrawer-paperAnchorRight': {
          borderRadius: 0,
          // my: 0,
          // mr: 2,
          // px: '10px',
          // px: '8px',
          // height: '100%',
          // backgroundColor: '#f8f9fa',
          // backgroundColor: '#ffffff',
          // overflowX: 'hidden',
          // overflowY: 'scroll',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '*::-webkit-scrollbar': {
          width: '0px !important',
        },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SCHEDULING_PANEL_WIDTH,
          boxSizing: 'border-box',
        },
      }}
      variant="persistent"
      anchor="right"
      open={open}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Toolbar />
      <Box
        sx={{
          pl: '10px',
        }}
      >
        <DrawerHeader>
          <Typography>Options</Typography>
        </DrawerHeader>
        <Grid container flexDirection="column" px="8px" spacing={2} width="100%" maxWidth="100%">
          <Grid item flexGrow={1} maxWidth="100%">
            {options.course_id && (
              <TextField
                fullWidth
                select
                label="Course"
                value={options.course_id}
                onChange={(event) => {
                  const newCourse = courses.find((course) => course.id === event.target.value);

                  if (!newCourse) {
                    return;
                  }

                  const newDefaultDuration = moment
                    .duration(newCourse?.typical_period_duration ?? { minutes: 60 })
                    .asMinutes();
                  const newDefaultCapacity = newCourse.typical_period_capacity ?? 1;
                  const newDefaultObservationCapacity = newCourse.typical_period_observation_capacity ?? null;
                  const newDefaultWaitlistCapacity = newCourse.typical_period_waitlist_capacity ?? null;
                  setSelectedCourse(newCourse);
                  setSelectedCourseType(newCourse?.course_type);
                  setOptions({
                    ...options,
                    course_id: event.target.value,
                    duration: newDefaultDuration,
                    capacity: newDefaultCapacity,
                    observationCapacity: newDefaultObservationCapacity ?? 0,
                    waitlistCapacity: newDefaultWaitlistCapacity ?? 0,
                    section_id: undefined,
                  });
                }}
                InputLabelProps={{ shrink: true }}
                variant="standard"
              >
                {courses
                  .filter((course) => course.course_type !== 'fully_online')
                  .map((course) => (
                    <MenuItem key={course.id} value={course.id}>
                      {course.title}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          </Grid>
          <Grid item flexGrow={1} maxWidth="100%">
            {options.section_id && (
              <TextField
                fullWidth
                select
                label="Section"
                value={options.section_id}
                onChange={(event) => {
                  const newSection = sectionRows?.find((section) => section.id === event.target.value);

                  if (!newSection) {
                    return;
                  }

                  // setSelectedSection(newSection);
                  setOptions({
                    ...options,
                    section_id: newSection.id,
                  });
                }}
                InputLabelProps={{ shrink: true }}
                variant="standard"
              >
                {sectionRows?.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.title}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item flexGrow={1} maxWidth="100%">
            <TextField
              fullWidth
              label="Period Duration"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">Minutes</InputAdornment>,
                inputProps: { min: 15 },
              }}
              variant="standard"
              type="number"
              InputLabelProps={{ shrink: true }}
              value={options.duration}
              onChange={(event) => {
                setOptions({
                  ...options,
                  duration: Number((event.target as HTMLInputElement).value),
                });
              }}
            />
          </Grid>

          {['self_scheduled', 'manual'].includes(selectedCourse?.schedule_type ?? '') && options.capacity && (
            <Grid item flexGrow={1} maxWidth="100%">
              <TextField
                fullWidth
                label="Registration Limit"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">Students</InputAdornment>,
                  inputProps: { min: 1 },
                }}
                variant="standard"
                type="number"
                InputLabelProps={{ shrink: true }}
                value={options.capacity}
                onChange={(event) => {
                  setOptions({
                    ...options,
                    capacity: Number((event.target as HTMLInputElement).value),
                  });
                }}
              />
            </Grid>
          )}

          {selectedCourse?.course_type === 'behind_the_wheel' && (
            <Grid item flexGrow={1} maxWidth="100%">
              <TextField
                fullWidth
                label="Observation Only Limit"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">Students</InputAdornment>,
                  inputProps: { min: 0 },
                }}
                variant="standard"
                type="number"
                InputLabelProps={{ shrink: true }}
                value={options.observationCapacity}
                onChange={(event) => {
                  setOptions({
                    ...options,
                    observationCapacity: Number((event.target as HTMLInputElement).value),
                  });
                }}
              />
            </Grid>
          )}

          {['self_scheduled'].includes(selectedCourse?.schedule_type ?? '') && (
            <>
              <Grid item flexGrow={1} maxWidth="100%">
                <TextField
                  fullWidth
                  label="Waitlist Limit"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Students</InputAdornment>,
                    inputProps: { min: 0 },
                  }}
                  variant="standard"
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  value={options.waitlistCapacity}
                  onChange={(event) => {
                    setOptions({
                      ...options,
                      waitlistCapacity: Number((event.target as HTMLInputElement).value),
                    });
                  }}
                />
              </Grid>
              <Grid item flexGrow={1} maxWidth="100%">
                <TextField
                  label="Open Registration"
                  variant="standard"
                  type="date"
                  fullWidth
                  InputProps={{ inputProps: { min: '2022-05-12' } }}
                  InputLabelProps={{ shrink: true }}
                  value={options.openRegistration.format('YYYY-MM-DD')}
                  onChange={(event) => {
                    setOptions({ ...options, openRegistration: moment(event.target.value) });
                  }}
                />
              </Grid>
              <Grid item flexGrow={1} maxWidth="100%">
                <TextField
                  fullWidth
                  label="Close Registration"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Minutes</InputAdornment>,
                  }}
                  variant="standard"
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  helperText="(Minutes Before Period Starts)"
                  value={options.closeRegistration}
                  // onChange={(event) => setSchedulingOptions({ ...schedulingOptions, registrationClose: Number((event.target as HTMLInputElement).value) })}
                />
              </Grid>
            </>
          )}
        </Grid>
        <DrawerHeader>
          <Typography>Instructors</Typography>
        </DrawerHeader>
        <List>
          {instructors &&
            instructors
              .filter((instructor) => instructor.user.active)
              .map((instructor) => {
                const labelId = `checkbox-list-secondary-label-${instructor.user_id}`;
                const className = visibleResources.has(instructor.user_id) ? 'check-instructor' : 'uncheck-instructor';
                return (
                  <Box key={instructor.user_id} my="2px">
                    <ListItem
                      id={`instructor-panel-item-${instructor.user_id}`}
                      className={className}
                      sx={{
                        borderLeft: 1,
                        borderWidth: '5px',
                        // borderColor: checked.indexOf(instructor.user_id) !== -1 ? instructor.calendarColor : `${instructor.calendarColor}7F`,
                        borderColor: instructor.calendar_color,
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        // transform: checked.indexOf(instructor.user_id) !== -1 ? 'translateX(-10px)' : 'translateX(0px)',
                        // opacity: disable ? '50%' : '100%',
                      }}
                      secondaryAction={
                        <Checkbox
                          // sx={{
                          //   opacity: disable ? '50%' : '100%',
                          // }}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                          disabled={disabled}
                          edge="end"
                          onChange={handleToggle(instructor.user_id)}
                          checked={visibleResources.has(instructor.user_id)}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      }
                      disablePadding
                    >
                      <ListItemButton onClick={handleToggle(instructor.user_id)}>
                        <Box>
                          {/* <ListItemText id={labelId} primary={`${instructor.firstName} ${instructor.lastName}`} /> */}
                          <Typography
                            variant="subtitle2"
                            m={-1}
                          >{`${instructor.user.nickname ?? instructor.user.first_name} ${instructor.user.last_name}`}</Typography>
                          <Typography
                            variant="caption"
                            pl={2}
                            m={-1}
                          >{`${getTotalEventCount(instructor.user_id)} Periods`}</Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    <Divider sx={{ ml: '10px' }} />
                  </Box>
                );
              })}
        </List>
      </Box>
      <Grid
        container
        flexDirection="column"
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#ffffff',
          p: '8px',
        }}
      >
        <Grid item flexGrow={1}>
          <ButtonGroup variant="contained" fullWidth size="small">
            {showUndo && (
              <IconButton aria-label="undo" color="primary" onClick={onUndo}>
                <UndoIcon />
              </IconButton>
            )}
            <Button disabled={disableSubmit} onClick={onSubmit}>
              Submit
            </Button>
            {/* <IconButton aria-label="redo"> */}
            {/*  <RedoIcon /> */}
            {/* </IconButton> */}
          </ButtonGroup>
        </Grid>
      </Grid>
    </Drawer>
  );
}
